/**
 * The chat landing's data (mission discovery): loads missions, follows the
 * /chat?mission= deep link, sorts active missions into running and recent,
 * and searches the missions the sidebar lists (agion/Agion#2873: drafts
 * included and marked, archived left out). Shared by the current landing and
 * the new chat's landing (PLAN-20260929 slice 7) so both read one
 * implementation.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { useMissionStore } from './useMissions'
import { useExecutionStore } from './useExecutionStore'
import { config } from '../utils/config'
import { isDraftMission, isListedMission } from '../utils/missionListing'
import type { Mission } from '../types/mission'

export const MAX_RECENT = 8
export const MAX_SEARCH_RESULTS = 20

/** A search result: a mission the sidebar lists, marked when it is a draft. */
export type LandingSearchResult = Mission & { isDraft: boolean }

export function useChatLandingModel() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { missions, loadMissions, isLoading, failure } = useMissionStore()
  const missionStates = useExecutionStore((s) => s.missionStates)
  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    loadMissions()
  }, [loadMissions])

  // Handle deep links
  const deepLinkMissionId = searchParams.get('mission')
  const autostart = searchParams.has('autostart')

  useEffect(() => {
    if (deepLinkMissionId) {
      const url = autostart
        ? `/mission/${deepLinkMissionId}?autostart=true`
        : `/mission/${deepLinkMissionId}`
      navigate(url, { replace: true })
    }
  }, [deepLinkMissionId, autostart, navigate])

  // Categorize missions
  const { activeMissions, runningMissions, recentMissions, searchResults } = useMemo(() => {
    const active = missions.filter(m => {
      const status = m.status || m.state
      return status === 'active'
    })

    // Running / HITL missions (need attention)
    const running = active.filter(m => {
      const state = missionStates.get(m.id)
      return state?.isRunning || state?.hasActiveHITL
    })

    // Recent: non-running active missions, sorted by last activity
    const recent = active
      .filter(m => {
        const state = missionStates.get(m.id)
        return !state?.isRunning && !state?.hasActiveHITL
      })
      .sort((a, b) => {
        const aTime = new Date(a.updatedAt || (a as { last_activity?: string }).last_activity || 0).getTime()
        const bTime = new Date(b.updatedAt || (b as { last_activity?: string }).last_activity || 0).getTime()
        return bTime - aTime
      })
      .slice(0, MAX_RECENT)

    // Search: the sidebar's missions, drafts included (agion/Agion#2873)
    const term = searchTerm.toLowerCase().trim()
    const results: LandingSearchResult[] = term
      ? missions
          .filter(isListedMission)
          .filter(m => m.name?.toLowerCase().includes(term) || m.description?.toLowerCase().includes(term))
          .slice(0, MAX_SEARCH_RESULTS)
          .map(m => ({ ...m, isDraft: isDraftMission(m) }))
      : []

    return {
      activeMissions: active,
      runningMissions: running,
      recentMissions: recent,
      searchResults: results,
    }
  }, [missions, missionStates, searchTerm])

  const handleSelectMission = useCallback((missionId: string) => {
    navigate(`/mission/${missionId}`)
  }, [navigate])

  const handleStartMission = useCallback((missionId: string) => {
    navigate(`/mission/${missionId}?autostart=true`)
  }, [navigate])

  const handleGoToAdmin = useCallback(() => {
    window.location.href = config.adminUrl
  }, [])

  return {
    isLoading,
    failure,
    retry: loadMissions,
    missionStates,
    searchTerm,
    setSearchTerm,
    isSearching: searchTerm.trim().length > 0,
    activeMissions,
    runningMissions,
    recentMissions,
    searchResults,
    handleSelectMission,
    handleStartMission,
    handleGoToAdmin,
  }
}
